import { Injectable, computed, signal } from "@angular/core";
import { UserData } from "@models/I_DecouplingUserData";
import { I_UserInfo } from "@models/I_UserInfo";

export interface PromoDataAll {
  promotionsActives: { promoId: string; promoType: string }[] | null;
  promotableErrors: string[] | null;
  isPromotable: boolean | null;
  isPromotableVip: boolean | null;
}

export enum GraceTimeStatus {
  ON_TIME,
  OUT_OF_TIME_REGISTER,
  OUT_OF_TIME_FIRST_DEPOSIT,
}

export interface ExtraUserData {
  graceTime?: GraceTimeStatus;
  documentationVerification?: boolean;
}

type DatesMap = {
  [key in keyof PromoDataAll]?: Date;
} & { userData?: Date } & { userInfo?: Date } & { [key in keyof ExtraUserData]?: Date };

const MINUST_PERSIST_DATA = 5;

@Injectable({
  providedIn: "root",
})
export class DataUserMSOService {
  private USER_INFO = signal<I_UserInfo | null>(null);
  private USER_PROMO_DATA = signal<PromoDataAll>({
    promotionsActives: null,
    promotableErrors: null,
    isPromotable: null,
    isPromotableVip: null,
  });
  private LAST_DATE_UPDATED = signal<DatesMap>({});
  private USER_DATA = signal<UserData | null>(null);
  private EXTRA_DATA = signal<ExtraUserData>({});

  get userInfo() {
    return computed(() => this.USER_INFO());
  }

  set dataUserInfo(userInfo: I_UserInfo) {
    this.LAST_DATE_UPDATED.update((last) => ({ ...last, userInfo: new Date() }));
    this.USER_INFO.update((_) => ({ ...userInfo }));
  }

  get getUserData() {
    return computed(() => this.USER_DATA());
  }

  set setUserData(userData: Partial<UserData>) {
    this.LAST_DATE_UPDATED.update((last) => ({ ...last, userData: new Date() }));
    this.USER_DATA.update((prevData) => ({ ...prevData, ...userData }));
  }

  get getUserPromoData() {
    return computed(() => ({ ...this.USER_PROMO_DATA() }));
  }

  set setUserPromoData(newData: Partial<PromoDataAll>) {
    const keysUpdated: DatesMap = {};
    Object.keys(newData).forEach((key) => {
      keysUpdated[key] = new Date();
    });
    this.LAST_DATE_UPDATED.update((last) => ({ ...last, ...keysUpdated }));
    this.USER_PROMO_DATA.update((oldData) => ({ ...oldData, ...newData }));
  }

  get getExtraUserData() {
    return computed(() => ({ ...this.EXTRA_DATA() }));
  }

  set setExtraUserData(userData: Partial<ExtraUserData>) {
    this.LAST_DATE_UPDATED.update((last) => ({ ...last, userData: new Date() }));
    this.EXTRA_DATA.update((prevData) => ({ ...prevData, ...userData }));
  }

  canUpdate(key: keyof DatesMap) {
    return computed(() => {
      if (!this.LAST_DATE_UPDATED()[key]) {
        return true;
      }
      const oldDate = new Date(this.LAST_DATE_UPDATED()[key].getTime());
      oldDate.setSeconds(MINUST_PERSIST_DATA * 60);
      const newDate = new Date();
      return newDate.getTime() > oldDate.getTime();
    });
  }

  reset() {
    this.USER_PROMO_DATA.set({
      isPromotable: null,
      isPromotableVip: null,
      promotionsActives: null,
      promotableErrors: null,
    });
    this.dataUserInfo = null;
    this.setUserData = null;
    this.setExtraUserData = {};
    this.LAST_DATE_UPDATED.set({});
  }
}
